(() => {
  'use strict';

  const STORAGE_KEY = 'bronxWishlistV1';
  const PRODUCT_PATH_PATTERN = /\/products\/([^/?#]+)/i;
  const HEART_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.8a5.3 5.3 0 0 0-7.5 0L12 6.1l-1.3-1.3a5.3 5.3 0 0 0-7.5 7.5L12 21l8.8-8.7a5.3 5.3 0 0 0 0-7.5Z"/></svg>';
  const productCache = new Map();
  let observer;
  let toastTimer;

  const safeString = (value) => (typeof value === 'string' ? value.trim() : '');
  const currentCurrency = () => window.Shopify?.currency?.active || 'GBP';

  function productHandleFromUrl(value) {
    try {
      const url = new URL(value || '', window.location.origin);
      const match = url.pathname.match(PRODUCT_PATH_PATTERN);
      return match ? decodeURIComponent(match[1]).toLowerCase() : '';
    } catch (_error) {
      return '';
    }
  }

  function productUrl(handle) {
    return handle ? `/products/${encodeURIComponent(handle)}` : '';
  }

  function normaliseItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const handle = safeString(raw.handle).toLowerCase();
    if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;

    return {
      handle,
      title: safeString(raw.title) || 'Saved product',
      url: productUrl(handle),
      image: safeString(raw.image),
      price: safeString(raw.price),
      variantId: String(raw.variantId || ''),
      available: raw.available !== false,
      addedAt: Number(raw.addedAt) || Date.now()
    };
  }

  function readWishlist() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      const unique = new Map();
      parsed.map(normaliseItem).filter(Boolean).forEach((item) => unique.set(item.handle, item));
      return Array.from(unique.values());
    } catch (_error) {
      return [];
    }
  }

  function writeWishlist(items) {
    const clean = items.map(normaliseItem).filter(Boolean);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
    } catch (_error) {
      showToast('Your browser could not save this wishlist.');
      return false;
    }
    document.dispatchEvent(new CustomEvent('bronx:wishlist:change', { detail: { items: clean } }));
    return true;
  }

  function hasProduct(handle) {
    return readWishlist().some((item) => item.handle === handle);
  }

  function formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currentCurrency(),
        minimumFractionDigits: 2
      }).format(amount / 100);
    } catch (_error) {
      return `£${(amount / 100).toFixed(2)}`;
    }
  }

  async function fetchProduct(handle) {
    if (!handle) throw new Error('Missing product handle');
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`${productUrl(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      const product = await response.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find((entry) => entry.available) || variants[0] || {};
      const image =
        safeString(product.featured_image) ||
        (Array.isArray(product.images) ? safeString(product.images[0]) : '');

      return normaliseItem({
        handle,
        title: product.title,
        url: productUrl(handle),
        image,
        price: formatMoney(product.price),
        variantId: variant.id,
        available: Boolean(product.available),
        addedAt: Date.now()
      });
    });

    productCache.set(handle, request);
    try {
      return await request;
    } catch (error) {
      productCache.delete(handle);
      throw error;
    }
  }

  function showToast(message) {
    let toast = document.querySelector('[data-bx-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bx-wishlist-toast';
      toast.dataset.bxWishlistToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2400);
  }

  function ensureHeaderCount() {
    document.querySelectorAll('.bx-nav-icon--heart').forEach((link) => {
      link.classList.add('bx-wishlist-header-link');
      if (!link.querySelector('[data-bx-wishlist-count]')) {
        const count = document.createElement('span');
        count.className = 'bx-wishlist-count';
        count.dataset.bxWishlistCount = '';
        count.hidden = true;
        count.textContent = '0';
        link.appendChild(count);
      }
    });

    const drawer = document.querySelector('.bx-drawer-extra');
    if (drawer && !drawer.querySelector('[data-bx-wishlist-mobile]')) {
      const link = document.createElement('a');
      link.href = '/pages/wishlist';
      link.dataset.bxWishlistMobile = '';
      link.append('Wishlist ');
      const count = document.createElement('span');
      count.dataset.bxWishlistCountInline = '';
      count.textContent = '(0)';
      link.appendChild(count);
      drawer.prepend(link);
    }
  }

  function updateCounts(items = readWishlist()) {
    const count = items.length;
    document.querySelectorAll('[data-bx-wishlist-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
      element.setAttribute('aria-label', `${count} saved ${count === 1 ? 'product' : 'products'}`);
    });
    document.querySelectorAll('[data-bx-wishlist-count-inline]').forEach((element) => {
      element.textContent = `(${count})`;
    });
    document.querySelectorAll('[data-bx-wishlist-page-count]').forEach((element) => {
      element.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    });
  }

  function updateToggleStates(items = readWishlist()) {
    const handles = new Set(items.map((item) => item.handle));
    document.querySelectorAll('[data-bx-wishlist-toggle]').forEach((button) => {
      const handle =
        safeString(button.dataset.productHandle).toLowerCase() ||
        productHandleFromUrl(button.dataset.productUrl || button.getAttribute('href'));
      if (!handle) return;

      const active = handles.has(handle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));

      const title = safeString(button.dataset.productTitle) || 'this product';
      button.setAttribute(
        'aria-label',
        active ? `Remove ${title} from wishlist` : `Add ${title} to wishlist`
      );

      const label = button.querySelector('[data-bx-wishlist-button-label]');
      if (label) label.textContent = active ? 'SAVED TO WISHLIST' : 'ADD TO WISHLIST';
    });
  }

  function syncInterface(items = readWishlist()) {
    ensureHeaderCount();
    updateCounts(items);
    updateToggleStates(items);
    renderWishlistPage(items);
  }

  function makeCardToggle(handle, url) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bx-wishlist-toggle bx-wishlist-toggle--card';
    button.dataset.bxWishlistToggle = '';
    button.dataset.productHandle = handle;
    button.dataset.productUrl = url || productUrl(handle);
    button.setAttribute('aria-label', 'Add product to wishlist');
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = HEART_ICON;
    return button;
  }

  function enhanceDawnCards(root = document) {
    root.querySelectorAll('.product-card-wrapper').forEach((card) => {
      if (card.dataset.bxWishlistReady === 'true') return;
      const link = card.querySelector('a[href*="/products/"]');
      const handle = productHandleFromUrl(link?.getAttribute('href'));
      if (!handle) return;

      card.dataset.bxWishlistReady = 'true';
      if (!card.querySelector('[data-bx-wishlist-toggle]')) {
        card.prepend(makeCardToggle(handle, link.href));
      }
    });
  }

  function enhanceBronxCards(root = document) {
    root.querySelectorAll('.bx-product-card').forEach((card) => {
      if (card.dataset.bxWishlistReady === 'true') return;
      const heart = card.querySelector('.bx-heart');
      if (!heart) return;

      const productLink = card.querySelector('a[href*="/products/"]');
      const handle = productHandleFromUrl(productLink?.getAttribute('href'));
      card.dataset.bxWishlistReady = 'true';

      if (!handle) {
        heart.classList.add('is-disabled');
        heart.setAttribute('aria-disabled', 'true');
        heart.addEventListener('click', (event) => event.preventDefault());
        return;
      }

      heart.dataset.bxWishlistToggle = '';
      heart.dataset.productHandle = handle;
      heart.dataset.productUrl = productLink.href;
      heart.dataset.productTitle = safeString(productLink.textContent);
      heart.setAttribute('role', 'button');
      heart.setAttribute('aria-pressed', 'false');
    });
  }

  function enhanceProductPage() {
    if (!PRODUCT_PATH_PATTERN.test(window.location.pathname)) return;
    const handle = productHandleFromUrl(window.location.pathname);
    if (!handle || document.querySelector('.bx-product-wishlist-button')) return;

    const information = document.querySelector('.product__info-container');
    const title = information?.querySelector('.product__title') || document.querySelector('.product__title');
    if (!information || !title) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bx-product-wishlist-button';
    button.dataset.bxWishlistToggle = '';
    button.dataset.productHandle = handle;
    button.dataset.productUrl = productUrl(handle);
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = `${HEART_ICON}<span data-bx-wishlist-button-label>ADD TO WISHLIST</span>`;
    title.insertAdjacentElement('afterend', button);
  }

  function enhanceInterface(root = document) {
    ensureHeaderCount();
    enhanceDawnCards(root);
    enhanceBronxCards(root);
    enhanceProductPage();
    updateToggleStates();
  }

  async function resolveProduct(button, handle) {
    const existing = readWishlist().find((item) => item.handle === handle);
    if (existing) return existing;

    const title = safeString(button.dataset.productTitle);
    const image = safeString(button.dataset.productImage);
    const price = safeString(button.dataset.productPrice);
    const variantId = safeString(button.dataset.variantId);

    if (title && (image || price || variantId)) {
      return normaliseItem({
        handle,
        title,
        image,
        price,
        variantId,
        available: button.dataset.productAvailable !== 'false',
        addedAt: Date.now()
      });
    }

    return fetchProduct(handle);
  }

  async function toggleFromButton(button) {
    const handle =
      safeString(button.dataset.productHandle).toLowerCase() ||
      productHandleFromUrl(button.dataset.productUrl || button.getAttribute('href'));

    if (!handle) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const current = readWishlist();
      const index = current.findIndex((item) => item.handle === handle);

      if (index >= 0) {
        const [removed] = current.splice(index, 1);
        if (writeWishlist(current)) showToast(`${removed.title} removed from your wishlist.`);
      } else {
        const product = await resolveProduct(button, handle);
        if (!product) throw new Error('Product data was unavailable');
        current.unshift({ ...product, addedAt: Date.now() });
        if (writeWishlist(current)) {
          button.classList.remove('is-just-added');
          void button.offsetWidth;
          button.classList.add('is-just-added');
          window.setTimeout(() => button.classList.remove('is-just-added'), 460);
          showToast(`${product.title} saved to your wishlist.`);
        }
      }
    } catch (_error) {
      showToast('This product could not be saved. Please try again.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      syncInterface();
    }
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function buildWishlistCard(item) {
    const card = createElement('article', 'bx-wishlist-card');
    card.dataset.productHandle = item.handle;

    const media = createElement('div', 'bx-wishlist-card__media');
    const mediaLink = createElement('a', 'bx-wishlist-card__image-link');
    mediaLink.href = item.url;
    mediaLink.setAttribute('aria-label', `View ${item.title}`);

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 900;
      image.height = 1125;
      mediaLink.appendChild(image);
    } else {
      mediaLink.appendChild(createElement('span', 'bx-wishlist-card__placeholder', 'BRONX'));
    }

    const remove = createElement('button', 'bx-wishlist-card__remove');
    remove.type = 'button';
    remove.dataset.bxWishlistRemove = item.handle;
    remove.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
    remove.innerHTML = HEART_ICON;
    media.append(mediaLink, remove);

    const content = createElement('div', 'bx-wishlist-card__content');
    const title = createElement('h2', 'bx-wishlist-card__title');
    const titleLink = createElement('a', '', item.title);
    titleLink.href = item.url;
    title.appendChild(titleLink);

    const price = createElement('p', 'bx-wishlist-card__price', item.price || '');
    if (!item.available) {
      content.append(title, price, createElement('p', 'bx-wishlist-card__status', 'CURRENTLY UNAVAILABLE'));
    } else {
      content.append(title, price);
    }

    const action = createElement('a', 'bx-wishlist-card__action', 'VIEW PRODUCT');
    action.href = item.url;
    content.appendChild(action);
    card.append(media, content);
    return card;
  }

  function renderWishlistPage(items = readWishlist()) {
    const page = document.querySelector('[data-bx-wishlist-page]');
    if (!page) return;

    const loading = page.querySelector('[data-bx-wishlist-loading]');
    const empty = page.querySelector('[data-bx-wishlist-empty]');
    const summary = page.querySelector('[data-bx-wishlist-summary]');
    const grid = page.querySelector('[data-bx-wishlist-grid]');

    if (loading) loading.hidden = true;
    if (!empty || !summary || !grid) return;

    grid.replaceChildren();
    const isEmpty = items.length === 0;
    empty.hidden = !isEmpty;
    summary.hidden = isEmpty;

    if (!isEmpty) {
      items.forEach((item) => grid.appendChild(buildWishlistCard(item)));
    }
    updateCounts(items);
  }

  function removeItem(handle) {
    const current = readWishlist();
    const item = current.find((entry) => entry.handle === handle);
    const next = current.filter((entry) => entry.handle !== handle);
    if (writeWishlist(next) && item) showToast(`${item.title} removed from your wishlist.`);
  }

  function bindEvents() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-bx-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        if (toggle.getAttribute('aria-disabled') === 'true' || toggle.disabled) return;
        toggleFromButton(toggle);
        return;
      }

      const remove = event.target.closest('[data-bx-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        removeItem(safeString(remove.dataset.bxWishlistRemove).toLowerCase());
        return;
      }

      const clear = event.target.closest('[data-bx-wishlist-clear]');
      if (clear) {
        event.preventDefault();
        if (window.confirm('Clear all saved products from your wishlist?')) {
          writeWishlist([]);
          showToast('Your wishlist has been cleared.');
        }
      }
    });

    document.addEventListener('bronx:wishlist:change', (event) => {
      syncInterface(event.detail?.items || readWishlist());
    });

    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) syncInterface();
    });

    document.addEventListener('shopify:section:load', (event) => {
      enhanceInterface(event.target);
      syncInterface();
    });
  }

  function observeDynamicContent() {
    if (!window.MutationObserver || observer) return;
    observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE)
      );
      if (!relevant) return;
      window.requestAnimationFrame(() => {
        enhanceInterface();
        updateToggleStates();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    enhanceInterface();
    bindEvents();
    syncInterface();
    observeDynamicContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
